import { Business, Group, Person } from "@material-ui/icons";
import styled from "styled-components";

import { WorkspaceType } from "models/workspace";

// Workspace type presentation, shared by the three places that render one: the nav selector,
// the Create Workspace dialog and the settings form. Both forms show the field as a select to
// power users and as a read-only input to everyone else — a dropdown whose only entry is the
// value already in it offers a choice the user does not have, which is what EVA-87 was about.
//
// The icons are keyed rather than a Personal/Organization ternary because Shared is a real type
// the app can be asked to display — an org-level workspace any user can open whoever owns it
// (getWorkspacePermissions, WorkspaceRepository.GetWorkspaces) — so a fall-through would show it
// wearing the Personal icon. Group is the icon Shared already carries for dashboards
// (getDashboardTypeIcon). Frozen as the map it replaced in WorkspaceNav was — the three call
// sites share these elements.
const WORKSPACE_TYPE_ICONS: Readonly<Record<WorkspaceType, JSX.Element>> = Object.freeze({
  Personal: <Person />,
  Organization: <Business />,
  Shared: <Group />
});

// Nothing for an absent or unrecognised type, leaving each caller to choose between a fallback
// icon and none: antd draws an empty prefix box for an element that renders nothing, so the
// read-only fields want the absence, while the nav wants a stand-in.
export const workspaceTypeIcon = (type: Maybe<WorkspaceType>): Maybe<JSX.Element> =>
  type ? WORKSPACE_TYPE_ICONS[type] : null;

const WorkspaceTypeItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

// Static, so it is built once here rather than memoised in each form. Not filtered by role:
// only power users are shown the select these fill. Shared is deliberately absent — it is a
// type the app displays but never lets anyone choose.
export const WORKSPACE_TYPE_OPTIONS: { label: JSX.Element; value: WorkspaceType }[] = [
  {
    label: (
      <WorkspaceTypeItem>{workspaceTypeIcon("Personal")} Personal</WorkspaceTypeItem>
    ),
    value: "Personal"
  },
  {
    label: (
      <WorkspaceTypeItem>
        {workspaceTypeIcon("Organization")} Organization
      </WorkspaceTypeItem>
    ),
    value: "Organization"
  }
];
